import React, { useEffect, useRef, useState } from 'react';
import { Button, Icon, Input, Switch, Table, Tooltip, Row, Col, notification, Modal, List, Tag } from 'antd';
import YearPicker from 'components/YearPicker/YearPicker';
import PrivateDownloadLink from 'components/PrivateDownloadLink/PrivateDownloadLink';
import moment from 'moment';
import { getSubInvoiceByYear } from 'utils/apis/billingInvoice';
import { buildAPHostBilling, buildAPHostDetails } from 'utils/routes';
import { BodyContainer, MainContainer } from './HostBilling.styles';
import { Link } from 'react-router-dom'; // Import Link for clickable links, if necessary
import { autobillingChanges } from 'utils/apis/host';
import { InfoCircleOutlined } from '@ant-design/icons';

const HostBilling = ({ history, location }) => {
  const [currentYear, setCurrentYear] = useState(moment().format('YYYY'));
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [changedRecords, setChangedRecords] = useState([]); // Store changed records
  const [hasChanges, setHasChanges] = useState(false); // Track if there are unsaved changes
  const [isModalVisible, setIsModalVisible] = useState(false); // State to control modal visibility

  const monthRefs = useRef([]);

  const searchInputRef = useRef(null);

  const setURLQuery = date => {
    const queryString = `year=${date}`;
    history.replace({
      pathname: buildAPHostBilling(),
      search: queryString
    });
  };

  const handleOnMonthChange = date => {
    const newYear = moment(date).format('YYYY');
    setCurrentYear(newYear);
    setURLQuery(newYear);
  };

  // Update changed records
  const handleSwitchChange = (checked, record) => {
    const updatedData = dataSource.map(item => {
      if (item._id === record._id) {
        return { ...item, autobilling: checked };
      }
      return item;
    });

    const originalRecord = invoices.find(item => item._id === record._id);

    const updatedChangedRecords = [...changedRecords];
    const existingIndex = updatedChangedRecords.findIndex(item => item._id === record._id);

    // If the current autobilling value matches the original, remove it from changedRecords
    if (originalRecord && originalRecord.autobilling === checked) {
      if (existingIndex > -1) {
        updatedChangedRecords.splice(existingIndex, 1); // Remove from changedRecords if it exists
      }
    } else {
      // Otherwise, update or add the record to changedRecords
      if (existingIndex > -1) {
        // If the record is already in changedRecords, update it
        updatedChangedRecords[existingIndex].autobilling = checked;
      } else {
        // If the record is not in changedRecords, add it
        updatedChangedRecords.push({ ...record, autobilling: checked });
      }
    }
    // console.log(updatedData, 99);

    setDataSource(updatedData);
    setChangedRecords(updatedChangedRecords); // Update the changedRecords state
    setHasChanges(updatedChangedRecords.length > 0); // Update the save button visibility
  };

  const handleSaveClick = () => {
    if (changedRecords.length > 0) {
      setIsModalVisible(true); // Show modal if there are changes
    }
  };

  const handleSave = async () => {
    try {
      // console.log('Save changes:', changedRecords);

      const update = await autobillingChanges(changedRecords);
      setIsModalVisible(false);
      window.location.reload();
    } catch (error) {
      console.error('Error saving changes:', error);
      notification.error({
        message: 'Save Failed',
        description: 'There was an issue saving your changes. Please try again later.'
      });
    }
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams(location.search);
        const urlYear = params.get('year');
        let invoices;
        let currentYear = urlYear;

        if (!urlYear) {
          currentYear = moment().format('YYYY');
          setCurrentYear(currentYear);
          setURLQuery(currentYear);
        } else {
          setCurrentYear(currentYear);
        }

        invoices = await getSubInvoiceByYear(currentYear);
        // console.log(invoices, 99);
        setDataSource(invoices);
        setInvoices(invoices);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [location.search]);

  useEffect(() => {
    const currentMonthIndex = moment().month();
    // const currentMonthIndex = moment('2025-11').month(); // Simulate November 2025 (index 10)

    // console.log(monthRefs.current, 99);
    if (monthRefs.current[currentMonthIndex]) {
      const targetCell = monthRefs.current[currentMonthIndex];

      // First scroll to the cell
      targetCell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });

      // console.log('Target cell:', targetCell);
    }
  }, [invoices]);

  const constructColumns = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const getLatestInvoice = invoices => {
      if (!invoices || invoices.length === 0) return null;
      return invoices.reduce((latest, invoice) => {
        return moment(invoice.invoiceDate).isAfter(moment(latest.invoiceDate)) ? invoice : latest;
      });
    };

    const monthColumns = months.map((month, index) => ({
      title: month,
      dataIndex: ['invoices', month],
      key: month,
      render: (invoices, record) => {
        // Always attach the ref, regardless of invoice count
        const baseProps = {
          style: { background: '#fff' }, // default background
          ref: el => (monthRefs.current[index] = el)
        };

        // Check if there are any invoices for this month
        if (!invoices || invoices.length === 0) {
          return {
            props: baseProps,
            children: '-'
          };
        }

        const allPaid = invoices.every(invoice => invoice.paymentStatus === 'paid');
        const allUnpaid = invoices.every(invoice => invoice.paymentStatus === 'unpaid');
        let totalAmount = 0;

        let backgroundColor = '#fff'; // default

        if (allPaid) {
          backgroundColor = '#d4edda'; // green
          totalAmount = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0);
        } else if (allUnpaid) {
          backgroundColor = '#f8d7da'; // red
          totalAmount = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0);
        } else {
          backgroundColor = '#fff3cd'; // orange (mixed)
          totalAmount = invoices
            .filter(invoice => invoice.paymentStatus === 'paid')
            .reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0);
        }

        // Create tooltip content showing all invoices
        const tooltipContent = (
          <div className="invoice-tooltip">
            {invoices.map((invoice, i) => (
              <div
                key={i}
                className="invoice-item"
                style={{
                  marginBottom: '10px',
                  borderBottom: i < invoices.length - 1 ? '1px solid #eee' : 'none',
                  paddingBottom: '8px'
                }}
              >
                <div>
                  <strong>{invoice.invoiceNo} - </strong>{' '}
                  <Tag color={invoice.paymentStatus === 'unpaid' ? 'red' : 'green'}>{invoice.paymentStatus === 'unpaid' ? 'Unpaid' : 'Paid'}</Tag>
                </div>
                <div>
                  Amount: {record.currency} {parseFloat(invoice.amount).toFixed(2)}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '8px' }}>
                  {/* P-INV Files section */}
                  <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>P-INV</div>
                  <div>:</div>
                  <div>
                    {invoice.files && invoice.files.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {invoice.files.map((file, fileIndex) => (
                          <PrivateDownloadLink key={fileIndex} file={file} />
                        ))}
                      </div>
                    ) : (
                      <div>No file available</div>
                    )}
                  </div>

                  {/* Official Invoice section */}
                  <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>Official Invoices</div>
                  <div>:</div>
                  <div>
                    {invoice.officialInvoice && invoice.officialInvoice.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {invoice.officialInvoice.map((file, fileIndex) => (
                          <PrivateDownloadLink key={fileIndex} file={file} />
                        ))}
                      </div>
                    ) : (
                      <div>No file available</div>
                    )}
                  </div>

                  {/* Official Receipt section */}
                  <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>Official Receipts</div>
                  <div>:</div>
                  <div>
                    {invoice.officialReceipt && invoice.officialReceipt.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {invoice.officialReceipt.map((file, fileIndex) => (
                          <PrivateDownloadLink key={fileIndex} file={file} />
                        ))}
                      </div>
                    ) : (
                      <div>No file available</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

        // Display total amount or count in the cell
        const displayText = `${record.currency} ${totalAmount.toFixed(2)} (${invoices.length})`;

        return {
          props: {
            style: { background: backgroundColor },
            ref: el => (monthRefs.current[index] = el)
          },
          children: (
            <Tooltip title={tooltipContent} placement="bottom">
              <div style={{ fontSize: '12px' }}>{displayText}</div>
            </Tooltip>
          )
        };
      }
    }));
    return [
      {
        title: 'Date Joined',
        dataIndex: 'createdAt',
        key: 'createdAt',
        fixed: 'left', // Fix this column to the left
        width: 150,
        render: (text, record) =>
          moment(record.createdAt)
            .add(8, 'hours')
            .format('DD-MM-YYYY'), // Display format

        // Add the sorter function
        sorter: (a, b) =>
          moment(a.createdAt)
            .add(8, 'hours')
            .unix() -
          moment(b.createdAt)
            .add(8, 'hours')
            .unix()
      },
      {
        title: 'Host Name',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left', // Fix this column to the left
        width: 250,
        sorter: (a, b) => a.name && b.name && a.name.localeCompare(b.name),
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div className="custom-filter-dropdown">
            <Input
              ref={searchInputRef} // Use the ref created above
              placeholder="Search host name"
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={confirm}
            />
            <Button type="primary" style={{ marginRight: '6px' }} onClick={confirm}>
              Search
            </Button>
            <Button onClick={clearFilters}>Reset</Button>
          </div>
        ),
        filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#00b9c6' : '#aaa' }} />,
        onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: visible => {
          if (visible) {
            setTimeout(() => {
              searchInputRef.current.focus(); // Focus the input when dropdown is visible
            });
          }
        },
        render: (text, record) => <Link to={`${buildAPHostDetails()}/${record._id}`}>{text}</Link>
      },
      {
        title: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            Autobilling
            <Tooltip title="Invoice would be generated on the -th of every month">
              <InfoCircleOutlined style={{ marginLeft: 5, color: 'black' }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'autobilling',
        fixed: 'left', // Fix this column to the left
        width: 120,
        render: (text, record) => (
          <Switch
            checked={record.autobilling}
            onChange={checked => handleSwitchChange(checked, record)} // Pass both checked value and record to handler
          />
        )
      },
      {
        title: 'Total Accumulated Outstanding',
        dataIndex: 'totalAccumulatedOutstanding',
        key: 'totalAccumulatedOutstanding',
        fixed: 'left',
        width: 140,
        render: (value, record) => {
          const backgroundColor = value > 0 ? '#f8d7da' : '#d4edda';
          const textColor = value > 0 ? '#721c24' : '#155724'; // Optional: readable text on bg
          let totalAmount = 0;
          totalAmount = record.totalAccumulatedOutstandingInvoice.reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0);

          const displayText = `${record.currency} ${totalAmount.toFixed(2)} (${record.totalAccumulatedOutstandingInvoice.length})`;

          const tooltipContent = (
            <div className="invoice-tooltip">
              {record.totalAccumulatedOutstandingInvoice.map((invoice, i) => (
                <div
                  key={i}
                  className="invoice-item"
                  style={{
                    marginBottom: '10px',
                    borderBottom: i < invoices.length - 1 ? '1px solid #eee' : 'none',
                    paddingBottom: '8px'
                  }}
                >
                  <div>
                    <strong>{invoice.invoiceNo} - </strong>{' '}
                    <Tag color={invoice.paymentStatus === 'unpaid' ? 'red' : 'green'}>{invoice.paymentStatus === 'unpaid' ? 'Unpaid' : 'Paid'}</Tag>
                  </div>
                  <div>
                    Amount: {record.currency} {parseFloat(invoice.amount).toFixed(2)}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '8px' }}>
                    {/* P-INV Files section */}
                    <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>P-INV</div>
                    <div>:</div>
                    <div>
                      {invoice.files && invoice.files.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {invoice.files.map((file, fileIndex) => (
                            <PrivateDownloadLink key={fileIndex} file={file} />
                          ))}
                        </div>
                      ) : (
                        <div>No file available</div>
                      )}
                    </div>

                    {/* Official Invoice section */}
                    <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>Official Invoices</div>
                    <div>:</div>
                    <div>
                      {invoice.officialInvoice && invoice.officialInvoice.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {invoice.officialInvoice.map((file, fileIndex) => (
                            <PrivateDownloadLink key={fileIndex} file={file} />
                          ))}
                        </div>
                      ) : (
                        <div>No file available</div>
                      )}
                    </div>

                    {/* Official Receipt section */}
                    <div style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>Official Receipts</div>
                    <div>:</div>
                    <div>
                      {invoice.officialReceipt && invoice.officialReceipt.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {invoice.officialReceipt.map((file, fileIndex) => (
                            <PrivateDownloadLink key={fileIndex} file={file} />
                          ))}
                        </div>
                      ) : (
                        <div>No file available</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );

          return {
            props: {
              style: { background: backgroundColor }
            },
            children:
              value > 0 ? (
                <Tooltip title={tooltipContent} placement="bottom">
                  <div style={{ fontSize: '12px' }}>{displayText}</div>
                </Tooltip>
              ) : (
                <div style={{ fontSize: '12px' }}>{displayText}</div>
              )
          };
        }
      },

      ...monthColumns // Add the month columns dynamically
    ];
  };

  return (
    <BodyContainer>
      <Row justify="space-between" align="middle">
        <Col span={16}>
          <YearPicker
            disabled={isLoading}
            todayButtonId="today-date-button3-report"
            label={'Year: '}
            defaultValue={moment(currentYear)}
            onChange={handleOnMonthChange}
          />
        </Col>
        <Col span={8} style={{ textAlign: 'right' }}>
          {' '}
          {/* Aligns content to the right */}
          <Button type="primary" onClick={handleSaveClick} disabled={!hasChanges}>
            Save
          </Button>
        </Col>
      </Row>

      <MainContainer>
        <Table
          bordered
          columns={constructColumns()}
          loading={isLoading}
          rowKey={record => record._id}
          dataSource={dataSource}
          scroll={{ x: 'max-content' }}
        />
        <Modal
          title="Confirm Autobilling Changes"
          visible={isModalVisible}
          onOk={handleSave}
          onCancel={() => setIsModalVisible(false)}
          okText="Confirm"
          cancelText="Cancel"
        >
          <p>The autobilling status has been modified for the following hosts:</p>
          <List
            dataSource={changedRecords}
            renderItem={item => (
              <List.Item>
                {item.name}:
                <Tag color={item.autobilling ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                  {item.autobilling ? 'Enabled' : 'Disabled'}
                </Tag>
              </List.Item>
            )}
          />
        </Modal>
      </MainContainer>
    </BodyContainer>
  );
};

export default HostBilling;
